import { tokenStorage } from "../auth";
import type { ContractSummaryView, ReleaseType, ReleaseStatus } from "@/types";

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api";

export const RELEASE_TYPES = {
  album: "Album",
  single: "Single",
  ep: "EP",
  compilation: "Compilation",
  soundtrack: "Soundtrack",
  other: "Other",
} as const satisfies Record<ReleaseType, string>;

export const RELEASE_STATUSES = {
  planning: "Planning",
  recording: "Recording",
  mixing: "Mixing",
  mastering: "Mastering",
  released: "Released",
  archived: "Archived",
} as const satisfies Record<ReleaseStatus, string>;

export interface PaginatedResponse {
  results: any[];
  next: string | null;
  previous: string | null;
  count: number;
}

export interface ReleaseQueryParams {
  label_id?: string;
  contributor_id?: string;
  contributor_ids?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
  q?: string;
  type?: string;
  title?: string;
  release_date_after?: string;
  release_date_before?: string;
}

export interface PartyFilters {
  label_id?: string;
  type?: "artist" | "label" | "organization";
  q?: string;
  country?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface RecordingFilters {
  label_id?: string;
  contributor_id?: string;
  contributor_ids?: string;
  release_ids?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
  q?: string;
  release_date_after?: string;
  release_date_before?: string;
  isrc?: string;
  has_isrc?: boolean;
  exclude_release_id?: string;
  no_contract?: boolean;
}

export interface ContractQueryParams {
  label_id?: string;
  party_id?: string;
  page?: number;
  page_size?: number;
  status?: string;
  contract_type?: string;
  q?: string;
  ordering?: string;
  start_date_after?: string;
  start_date_before?: string;
  end_date_after?: string;
  end_date_before?: string;
  end_date_active?: boolean;
  without_tracks?: boolean;
  summary?: ContractSummaryView;
}

export function cleanEmptyFields(object: any): any {
  return Object.fromEntries(Object.entries(object).filter(([, value]) => value !== ""));
}

export const rawApiRequest = async <T>(endpoint: string, options: RequestInit = {}): Promise<T> => {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(options.headers as Record<string, string>),
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const errorWithStatus = new Error(body.detail || body.message || `HTTP ${response.status}`);
    (errorWithStatus as any).status = response.status;
    (errorWithStatus as any).body = body;
    throw errorWithStatus;
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
};

// Single-flight refresh promise to prevent concurrent refresh races
let refreshPromise: Promise<void> | null = null;

const refreshWithToken = (refreshToken: string) => {
  return rawApiRequest<{ access: string; refresh: string }>("/accounts/auth/jwt/refresh/", {
    method: "POST",
    body: JSON.stringify({ refresh: refreshToken }),
  });
};

const withCrossTabRefreshLock = async <T>(callback: () => Promise<T>): Promise<T> => {
  const locksApi = (globalThis.navigator as Navigator & {
    locks?: {
      request: <U>(
        name: string,
        options: { mode: "exclusive" },
        worker: () => Promise<U>,
      ) => Promise<U>;
    };
  }).locks;

  if (!locksApi?.request) {
    return callback();
  }

  return locksApi.request("track-auth-refresh", { mode: "exclusive" }, callback);
};

const refreshAccessToken = async (): Promise<void> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = withCrossTabRefreshLock(async () => {
    // Re-check token inside lock - another tab may have already refreshed
    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken) {
      const err: any = new Error("No refresh token available");
      err.status = 401;
      throw err;
    }

    try {
      const newTokens = await refreshWithToken(refreshToken);
      tokenStorage.set(newTokens);
      return;
    } catch (error: any) {
      // Non-401 errors (network, server) - don't clear tokens, let caller retry
      if (error?.status !== 401) {
        throw error;
      }

      // 401 means token was rejected. Check if another tab rotated it.
      const latestToken = tokenStorage.getRefreshToken();
      if (latestToken && latestToken !== refreshToken) {
        // Another tab rotated the token - retry with the new one
        try {
          const newTokens = await refreshWithToken(latestToken);
          tokenStorage.set(newTokens);
          return;
        } catch (retryError: any) {
          if (retryError?.status === 401) {
            // Definitive failure - the new token is also invalid
            tokenStorage.remove();
          }
          throw retryError;
        }
      }

      // Same token or no token - this is a definitive auth failure
      tokenStorage.remove();
      throw error;
    }
  }).finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

// Extract field-level validation errors from DRF responses (e.g. {"file": ["error message"]})
export function extractFieldErrors(error: Record<string, unknown>): string | null {
  const messages: string[] = [];
  for (const value of Object.values(error)) {
    if (Array.isArray(value)) {
      messages.push(...value.map(String));
    } else if (typeof value === "string") {
      messages.push(value);
    }
  }
  return messages.length > 0 ? messages.join(" ") : null;
}

// Authenticated API request that handles token refresh (with single-flight)
export const authenticatedApiRequest = async <T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> => {
  const url = `${API_BASE_URL}${endpoint}`;

  const makeRequest = async (): Promise<T> => {
    const accessToken = tokenStorage.getAccessToken();

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    // Only set Content-Type for non-FormData requests
    if (!(options.body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    // Always attach the token if present; let the server validate expiry
    if (accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
    }

    const response = await fetch(url, { ...options, headers });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const message = error.detail || error.message || extractFieldErrors(error) || `HTTP ${response.status}`;
      const errorWithStatus = new Error(message);
      (errorWithStatus as any).status = response.status;
      (errorWithStatus as any).fieldErrors = error;
      throw errorWithStatus;
    }

    if (response.status === 204) {
      return undefined as T;
    }
    return response.json();
  };

  try {
    return await makeRequest();
  } catch (error: any) {
    // If 401, attempt a single-flight refresh then retry once
    if (error?.status === 401) {
      await refreshAccessToken();
      return await makeRequest();
    }
    throw error;
  }
};

export const apiRequest = authenticatedApiRequest;
